"use client"

import { useState, useEffect } from "react"
import "./BakeryCatalog.css"

// Import JSON data
import productsData from "../data/products.json"
import categoriesData from "../data/categories.json"
import typesData from "../data/types.json"
import priceRangesData from "../data/price-ranges.json"

// Import the useCart hook
import { useCart } from "../Context/CartContext"

export default function BakeryCatalog() {
  // Get data from JSON files
  const allProducts = productsData.products
  const categories = categoriesData.categories
  const types = typesData.types
  const priceRanges = priceRangesData.priceRanges

  // Get cart functions from context
  const { addToCart, isItemMarked } = useCart()

  const [selectedCategory, setSelectedCategory] = useState("ALL")
  const [selectedType, setSelectedType] = useState("All")
  const [selectedPriceRange, setSelectedPriceRange] = useState(priceRanges[0])
  const [filteredProducts, setFilteredProducts] = useState([])
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [addedToCart, setAddedToCart] = useState({})

  useEffect(() => {
    // Simulate loading data
    setIsLoading(true)

    // Small timeout to simulate data loading
    const timer = setTimeout(() => {
      filterProducts()
      setIsLoading(false)
    }, 300)

    return () => clearTimeout(timer)
  }, [selectedCategory, selectedType, selectedPriceRange])

  const filterProducts = () => {
    let filtered = [...allProducts]

    // Filter by category
    if (selectedCategory !== "ALL") {
      filtered = filtered.filter((product) => product.category === selectedCategory)
    }

    // Filter by type
    if (selectedType !== "All") {
      filtered = filtered.filter((product) => product.type === selectedType)
    }

    // Filter by price range
    filtered = filtered.filter(
      (product) => product.price >= selectedPriceRange.min && product.price <= selectedPriceRange.max,
    )

    setFilteredProducts(filtered)
  }

  const handleCategoryChange = (category) => {
    setSelectedCategory(category)
    setIsMenuOpen(false)
  }

  const handleTypeChange = (type) => {
    setSelectedType(type)
  }

  const handlePriceRangeChange = (priceRange) => {
    setSelectedPriceRange(priceRange)
  }

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen)
  }

  // Handle adding product to cart with animation
  const handleAddToCart = (product) => {
    addToCart(product, 1)

    // Show added animation
    setAddedToCart({ ...addedToCart, [product.id]: true })

    // Reset animation after 1.5 seconds
    setTimeout(() => {
      setAddedToCart({ ...addedToCart, [product.id]: false })
    }, 1500)
  }

  if (isLoading) {
    return (
      <div className="bakery-catalog loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading products...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="bakery-catalog">
      {/* Category Navigation */}
      <section className="category-nav">
        <div className="category-tabs">
          {categories.map((category) => (
            <button
              key={category}
              className={`category-tab ${selectedCategory === category ? "active" : ""}`}
              onClick={() => handleCategoryChange(category)}
            >
              {category}
            </button>
          ))}
        </div>
      </section>

      {/* Filters */}
      <section className="filters-section">
        <div className="filter-group">
          <h3>Type</h3>
          <div className="filter-options">
            {types.map((type) => (
              <button
                key={type}
                className={`filter-option ${selectedType === type ? "active" : ""}`}
                onClick={() => handleTypeChange(type)}
              >
                {type}
              </button>
            ))}
          </div>
        </div>
        <div className="filter-group">
          <h3>Price</h3>
          <div className="filter-options">
            {priceRanges.map((range, index) => (
              <button
                key={index}
                className={`filter-option ${selectedPriceRange.label === range.label ? "active" : ""}`}
                onClick={() => handlePriceRangeChange(range)}
              >
                {range.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="products-section">
        <h2 className="section-title">{selectedCategory === "ALL" ? "All Products" : selectedCategory}</h2>

        {filteredProducts.length === 0 ? (
          <div className="no-products">
            <p>No products match your current filters. Try adjusting your selection.</p>
          </div>
        ) : (
          <div className="products-grid">
            {filteredProducts.map((product) => (
              <div className="product-card" key={product.id}>
                <div className="product-image">
                  <img src={product.image || "/placeholder.svg"} alt={product.name} />
                  {isItemMarked(product.id) && (
                    <div className="product-marked">
                      <span>✓ In Cart</span>
                    </div>
                  )}
                </div>
                <div className="product-details">
                  <h3 className="product-name">{product.name}</h3>
                  <span className="product-price">₹{product.price}</span>
                  <span className={`product-type ${product.type.toLowerCase()}`}>{product.type}</span>
                  <p className="product-description">{product.description}</p>
                  <button
                    className={`add-to-cart-btn ${addedToCart[product.id] ? "added" : ""} ${isItemMarked(product.id) ? "in-cart" : ""}`}
                    onClick={() => handleAddToCart(product)}
                  >
                    {addedToCart[product.id] ? "Added to Cart!" : isItemMarked(product.id) ? "Add More" : "Add to Cart"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  )
}

